import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../../../lib/api';
import ModaleCercaRecord from '../../../../components/ModaleCercaRecord';
import { API_DOCS } from './TabCliente.api';
import ApiInspector from '../../../../components/ApiInspector';
import BancaFormFields from '../../../../components/BancaFormFields';
import SottocontoCogeInfo from '../../../../components/SottocontoCogeInfo';

// ── Icons ─────────────────────────────────────────────────────────────────
const EditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

// ── Interfaces ────────────────────────────────────────────────────────────
interface CondizionePagamentoLite {
  id: number;
  nome: string;
  canale: string;
}

interface CodiceIvaLite {
  id: number;
  codice: string;
  descrizione: string;
  aliquota: number | string;
  predefinito?: boolean;
}

interface Cliente {
  categoriaCliente: string;
  accountId?: number;
  insegnaClienteId?: number;
  agenteNominativoId?: number;
  condizionePagamentoId?: number;
  codiceIvaDefaultId?: number;
  codiceIvaDefault?: { id: number; codice: string; descrizione: string; aliquota: number | string };
  iban?: string;
  abi?: string;
  cab?: string;
  banca?: string;
  filialeBanca?: string;
  account?: {
    id: number;
    nome: string;
    cognome: string;
    email: string;
  };
  insegnaCliente?: {
    id: number;
    nome: string;
    codice: string;
  };
  agenteNominativo?: {
    id: number;
    codice: string;
    ragioneSociale?: string;
    nome?: string;
  };
  condizionePagamento?: CondizionePagamentoLite;
  note?: string;
}

interface Email {
  id: number;
  etichetta: string;
  email: string;
  principale: boolean;
  ambito?: 'GENERALE' | 'CLIENTE' | 'FORNITORE';
}

interface Contatto {
  id: number;
  nome: string;
  cognome?: string;
  ruolo?: string;
  telefono?: string;
  cellulare?: string;
  email?: string;
  principale: boolean;
  ambito?: 'GENERALE' | 'CLIENTE' | 'FORNITORE';
}

interface Filiale {
  id: number;
  nome: string;
  via?: string;
  cap?: string;
  citta?: string;
  provincia?: string;
  paese?: string;
  note?: string;
  attivo: boolean;
}

interface TabClienteProps {
  nominativoId: number;
  societaId: number;
  cliente: Cliente;
  email: Email[];
  contatti: Contatto[];
  filiali: Filiale[];
  onReload: () => void;
}

// ── Componente ────────────────────────────────────────────────────────────
export default function TabCliente({ nominativoId, societaId, cliente, email, contatti, filiali, onReload }: TabClienteProps) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    categoriaCliente: cliente.categoriaCliente,
    accountId: cliente.accountId ?? null,
    insegnaClienteId: cliente.insegnaClienteId ?? null,
    agenteNominativoId: cliente.agenteNominativoId ?? null,
    condizionePagamentoId: cliente.condizionePagamentoId ?? null,
    codiceIvaDefaultId: cliente.codiceIvaDefaultId ?? null,
    iban: cliente.iban || '',
    abi: cliente.abi || '',
    cab: cliente.cab || '',
    banca: cliente.banca || '',
    filialeBanca: cliente.filialeBanca || '',
    note: cliente.note || '',
  });

  // Dati caricati per le modali
  const [utenti, setUtenti] = useState<any[]>([]);
  const [insegne, setInsegne] = useState<any[]>([]);
  const [agenti, setAgenti] = useState<any[]>([]);
  const [condizioniPagamento, setCondizioniPagamento] = useState<CondizionePagamentoLite[]>([]);
  const [codiciIva, setCodiciIva] = useState<CodiceIvaLite[]>([]);

  // Dati selezionati (per visualizzazione)
  const [selectedAccount, setSelectedAccount] = useState<any | null>(null);
  const [selectedInsegna, setSelectedInsegna] = useState<any | null>(null);
  const [selectedAgente, setSelectedAgente] = useState<any | null>(null);

  // Modali aperte
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showInsegnaModal, setShowInsegnaModal] = useState(false);
  const [showAgenteModal, setShowAgenteModal] = useState(false);

  // ── Stato Email / Contatti / Filiali ──────────────────────────────────────
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showContattoModal, setShowContattoModal] = useState(false);
  const [showFilialeModal, setShowFilialeModal] = useState(false);
  const [editingEmail, setEditingEmail] = useState<Email | null>(null);
  const [editingContatto, setEditingContatto] = useState<Contatto | null>(null);
  const [editingFiliale, setEditingFiliale] = useState<Filiale | null>(null);
  const [savingRelaz, setSavingRelaz] = useState(false);

  const [emailForm, setEmailForm] = useState({
    etichetta: '',
    email: '',
    principale: false,
  });

  const [contattoForm, setContattoForm] = useState({
    nome: '',
    cognome: '',
    ruolo: '',
    telefono: '',
    cellulare: '',
    email: '',
    principale: false,
  });

  const [filialeForm, setFilialeForm] = useState({
    nome: '',
    via: '',
    cap: '',
    citta: '',
    provincia: '',
    paese: 'Italia',
    note: '',
    attivo: true,
  });

  const emailCliente = email.filter(e => e.ambito === 'CLIENTE');
  const contattiCliente = contatti.filter(c => c.ambito === 'CLIENTE');

  const getCategoriaLabel = (categoria: string) => {
    switch (categoria) {
      case 'CATENA_GDO': return 'Catena GDO';
      case 'AZIENDA_B2B': return 'Azienda B2B';
      case 'PRIVATO_B2C': return 'Privato B2C';
      case 'AGENTE': return 'Agente';
      default: return categoria;
    }
  };

  const formatIBAN = (iban: string) => iban.replace(/(.{4})/g, '$1 ').trim();

  const getCategoriaBadgeClass = (categoria: string): string => {
    switch (categoria) {
      case 'CATENA_GDO':
        return 'bop-tab-cliente__badge--gdo';
      case 'AZIENDA_B2B':
        return 'bop-tab-cliente__badge--b2b';
      case 'PRIVATO_B2C':
        return 'bop-tab-cliente__badge--b2c';
      case 'AGENTE':
        return 'bop-tab-cliente__badge--agente';
      default:
        return 'bop-badge bop-badge--neutral';
    }
  };

  // Carica dati quando si apre la modale di modifica
  useEffect(() => {
    if (showEditModal) {
      loadUtenti();
      loadInsegne();
      loadAgenti();
      loadCondizioniPagamento();
      loadCodiciIva();
    }
  }, [showEditModal]);

  const loadUtenti = async () => {
    try {
      // @api GET /api/utenti/selezionabili?societaId=${societaId} -> {"success":"true","data":[{"id":"number","nome":"string"}]}
      const data = await apiFetch<{ success: boolean; data: { id: number; nome: string }[] }>(`/api/utenti/selezionabili?societaId=${societaId}`);
      setUtenti(data.data ?? []);
    } catch (err) {
      console.error('Errore caricamento utenti:', err);
      setUtenti([]);
    }
  };

  const loadInsegne = async () => {
    try {
      // @api GET /api/anagrafiche/insegne?societaId=${societaId} -> {"insegne":"Insegna[]"}
      const data = await apiFetch<any>(`/api/anagrafiche/insegne?societaId=${societaId}`);
      const insegneArray = Array.isArray(data) ? data : (data.insegne ?? data.data ?? []);
      setInsegne(insegneArray);
    } catch (err) {
      console.error('Errore caricamento insegne:', err);
      setInsegne([]);
    }
  };

  const loadAgenti = async () => {
    try {
      // @api GET /api/nominativi?categoriaFornitore=AGENTE&societaId=${societaId} -> {"data":"Nominativo[]"}
      const data = await apiFetch<any>(`/api/nominativi?categoriaFornitore=AGENTE&societaId=${societaId}`);
      const agentiArray = Array.isArray(data) ? data : (data.data ?? data.nominativi ?? []);
      setAgenti(agentiArray);
    } catch (err) {
      console.error('Errore caricamento agenti:', err);
      setAgenti([]);
    }
  };

  const loadCondizioniPagamento = async () => {
    try {
      // @api GET /api/nominativi/opzioni/condizioni-pagamento?societaId=${societaId} -> {"success":"boolean","data":"CondizionePagamentoLite[]"}
      const res = await apiFetch<any>(`/api/nominativi/opzioni/condizioni-pagamento?societaId=${societaId}`);
      const arr = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setCondizioniPagamento(arr);
    } catch (err) {
      console.error('Errore caricamento condizioni pagamento:', err);
      setCondizioniPagamento([]);
    }
  };

  const loadCodiciIva = async () => {
    try {
      // @api GET /api/nominativi/opzioni/codici-iva?societaId=${societaId} -> {"success":"boolean","data":"CodiceIvaLite[]"}
      const res = await apiFetch<any>(`/api/nominativi/opzioni/codici-iva?societaId=${societaId}`);
      const arr = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      setCodiciIva(arr);
    } catch (err) {
      console.error('Errore caricamento codici IVA:', err);
      setCodiciIva([]);
    }
  };

  const openEditModal = () => {
    setForm({
      categoriaCliente: cliente.categoriaCliente,
      accountId: cliente.accountId ?? null,
      insegnaClienteId: cliente.insegnaClienteId ?? null,
      agenteNominativoId: cliente.agenteNominativoId ?? null,
      condizionePagamentoId: cliente.condizionePagamentoId ?? null,
      codiceIvaDefaultId: cliente.codiceIvaDefaultId ?? null,
      iban: cliente.iban || '',
      abi: cliente.abi || '',
      cab: cliente.cab || '',
      banca: cliente.banca || '',
      filialeBanca: cliente.filialeBanca || '',
      note: cliente.note || '',
    });
    setSelectedAccount(cliente.account || null);
    setSelectedInsegna(cliente.insegnaCliente || null);
    setSelectedAgente(cliente.agenteNominativo || null);
    setShowEditModal(true);
  };

  // ── Handlers Email ────────────────────────────────────────────────────────
  const openEmailModal = (em?: Email) => {
    if (em) {
      setEditingEmail(em);
      setEmailForm({ etichetta: em.etichetta, email: em.email, principale: em.principale });
    } else {
      setEditingEmail(null);
      setEmailForm({ etichetta: '', email: '', principale: false });
    }
    setShowEmailModal(true);
  };

  const saveEmail = async () => {
    setSavingRelaz(true);
    try {
      const payload = { ...emailForm, ambito: 'CLIENTE' };
      if (editingEmail) {
        // @api PUT /api/nominativi/:id/email/:id {"etichetta":"string","email":"string","principale":"boolean","ambito":"string"} -> {"ok":"boolean"}
        await apiFetch(`/api/nominativi/${nominativoId}/email/${editingEmail.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        // @api POST /api/nominativi/:id/email {"etichetta":"string","email":"string","principale":"boolean","ambito":"string"} -> {"id":"number"}
        await apiFetch(`/api/nominativi/${nominativoId}/email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      setShowEmailModal(false);
      await onReload();
    } catch (err: any) {
      alert(err.message || 'Errore durante il salvataggio');
    } finally {
      setSavingRelaz(false);
    }
  };

  const deleteEmail = async (id: number) => {
    if (!window.confirm('Eliminare questa email?')) return;
    try {
      // @api DELETE /api/nominativi/:id/email/:id -> {"ok":"boolean"}
      await apiFetch(`/api/nominativi/${nominativoId}/email/${id}`, { method: 'DELETE' });
      await onReload();
    } catch (err: any) {
      alert(err.message || 'Errore durante l\'eliminazione');
    }
  };

  // ── Handlers Contatti ─────────────────────────────────────────────────────
  const openContattoModal = (c?: Contatto) => {
    if (c) {
      setEditingContatto(c);
      setContattoForm({
        nome: c.nome,
        cognome: c.cognome || '',
        ruolo: c.ruolo || '',
        telefono: c.telefono || '',
        cellulare: c.cellulare || '',
        email: c.email || '',
        principale: c.principale,
      });
    } else {
      setEditingContatto(null);
      setContattoForm({ nome: '', cognome: '', ruolo: '', telefono: '', cellulare: '', email: '', principale: false });
    }
    setShowContattoModal(true);
  };

  const saveContatto = async () => {
    setSavingRelaz(true);
    try {
      const payload = { ...contattoForm, ambito: 'CLIENTE' };
      if (editingContatto) {
        // @api PUT /api/nominativi/:id/contatti/:id {"nome":"string","cognome":"string","ruolo":"string","telefono":"string","cellulare":"string","email":"string","principale":"boolean","ambito":"string"} -> {"ok":"boolean"}
        await apiFetch(`/api/nominativi/${nominativoId}/contatti/${editingContatto.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        // @api POST /api/nominativi/:id/contatti {"nome":"string","cognome":"string","ruolo":"string","telefono":"string","cellulare":"string","email":"string","principale":"boolean","ambito":"string"} -> {"id":"number"}
        await apiFetch(`/api/nominativi/${nominativoId}/contatti`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      setShowContattoModal(false);
      await onReload();
    } catch (err: any) {
      alert(err.message || 'Errore durante il salvataggio');
    } finally {
      setSavingRelaz(false);
    }
  };

  const deleteContatto = async (id: number) => {
    if (!window.confirm('Eliminare questo contatto?')) return;
    try {
      // @api DELETE /api/nominativi/:id/contatti/:id -> {"ok":"boolean"}
      await apiFetch(`/api/nominativi/${nominativoId}/contatti/${id}`, { method: 'DELETE' });
      await onReload();
    } catch (err: any) {
      alert(err.message || 'Errore durante l\'eliminazione');
    }
  };

  // ── Handlers Filiali ──────────────────────────────────────────────────────
  const openFilialeModal = (fil?: Filiale) => {
    if (fil) {
      setEditingFiliale(fil);
      setFilialeForm({
        nome: fil.nome,
        via: fil.via || '',
        cap: fil.cap || '',
        citta: fil.citta || '',
        provincia: fil.provincia || '',
        paese: fil.paese || 'Italia',
        note: fil.note || '',
        attivo: fil.attivo,
      });
    } else {
      setEditingFiliale(null);
      setFilialeForm({ nome: '', via: '', cap: '', citta: '', provincia: '', paese: 'Italia', note: '', attivo: true });
    }
    setShowFilialeModal(true);
  };

  const saveFiliale = async () => {
    setSavingRelaz(true);
    try {
      if (editingFiliale) {
        // @api PUT /api/nominativi/:id/filiali/:id {"nome":"string","via":"string","cap":"string","citta":"string","provincia":"string","paese":"string","note":"string","attivo":"boolean"} -> {"ok":"boolean"}
        await apiFetch(`/api/nominativi/${nominativoId}/filiali/${editingFiliale.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(filialeForm),
        });
      } else {
        // @api POST /api/nominativi/:id/filiali {"nome":"string","via":"string","cap":"string","citta":"string","provincia":"string","paese":"string","note":"string","attivo":"boolean"} -> {"ok":"boolean"}
        await apiFetch(`/api/nominativi/${nominativoId}/filiali`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(filialeForm),
        });
      }
      setShowFilialeModal(false);
      await onReload();
    } catch (err: any) {
      alert(err.message || 'Errore durante il salvataggio');
    } finally {
      setSavingRelaz(false);
    }
  };

  const deleteFiliale = async (id: number) => {
    if (!window.confirm('Disattivare questa filiale?')) return;
    try {
      // @api DELETE /api/nominativi/:id/filiali/:id -> {"ok":"boolean"}
      await apiFetch(`/api/nominativi/${nominativoId}/filiali/${id}`, { method: 'DELETE' });
      await onReload();
    } catch (err: any) {
      alert(err.message || 'Errore durante l\'eliminazione');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // @api PUT /api/nominativi/:id/cliente {"categoriaCliente":"string","accountId":"number","insegnaClienteId":"number","agenteNominativoId":"number","condizionePagamentoId":"number","codiceIvaDefaultId":"number","iban":"string","abi":"string","cab":"string","banca":"string","filialeBanca":"string","note":"string"} -> {"ok":"boolean"}
      await apiFetch(`/api/nominativi/${nominativoId}/cliente`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          categoriaCliente: form.categoriaCliente,
          accountId: form.accountId,
          insegnaClienteId: form.insegnaClienteId,
          agenteNominativoId: form.agenteNominativoId,
          condizionePagamentoId: form.condizionePagamentoId,
          codiceIvaDefaultId: form.codiceIvaDefaultId,
          iban: form.iban?.trim() || null,
          abi: form.abi?.trim() || null,
          cab: form.cab?.trim() || null,
          banca: form.banca?.trim() || null,
          filialeBanca: form.filialeBanca?.trim() || null,
          note: form.note || null,
        }),
      });
      setShowEditModal(false);
      await onReload();
    } catch (err: any) {
      alert(err.message || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Informazioni Cliente */}
      <div className="bop-tab-cliente__card">
        <h3 className="bop-tab-cliente__section-title">
          Dati Cliente
          <button className="bop-btn bop-btn-primary" onClick={openEditModal}>
            <EditIcon />
            Modifica
          </button>
        </h3>

        <div className="bop-tab-cliente__field-grid">
          {/* Categoria Cliente */}
          <div>
            <div className="bop-tab-cliente__field-label">Categoria Cliente</div>
            <div className="bop-tab-cliente__field-value">
              <span className={`bop-tab-cliente__badge ${getCategoriaBadgeClass(cliente.categoriaCliente)}`}>
                {getCategoriaLabel(cliente.categoriaCliente)}
              </span>
            </div>
          </div>

          {/* Account Manager */}
          <div>
            <div className="bop-tab-cliente__field-label">Account Manager Assegnato</div>
            <div className="bop-tab-cliente__field-value">
              {cliente.account ? (
                <span>{cliente.account.nome} {cliente.account.cognome}</span>
              ) : (
                <span className="bop-tab-cliente__empty-text">Nessun account manager assegnato</span>
              )}
            </div>
          </div>

          {/* Insegna Cliente */}
          <div>
            <div className="bop-tab-cliente__field-label">Insegna</div>
            <div className="bop-tab-cliente__field-value">
              {cliente.insegnaCliente ? (
                <div className="bop-tab-cliente__insegna-row">
                  <span className="bop-tab-cliente__badge bop-tab-cliente__badge--insegna">{cliente.insegnaCliente.codice}</span>
                  <span>{cliente.insegnaCliente.nome}</span>
                </div>
              ) : (
                <span className="bop-tab-cliente__empty-text">Nessuna insegna associata</span>
              )}
            </div>
          </div>

          {/* Agente Assegnato */}
          <div>
            <div className="bop-tab-cliente__field-label">Agente Assegnato</div>
            <div className="bop-tab-cliente__field-value">
              {cliente.agenteNominativo ? (
                <span>
                  {cliente.agenteNominativo.codice} — {cliente.agenteNominativo.ragioneSociale || cliente.agenteNominativo.nome}
                </span>
              ) : (
                <span className="bop-tab-cliente__empty-text">Nessun agente assegnato</span>
              )}
            </div>
          </div>
        </div>

        {/* Note */}
        {cliente.note && (
          <div className="bop-tab-cliente__note-section">
            <div className="bop-tab-cliente__field-label">Note Cliente</div>
            <div className="bop-tab-cliente__info-box bop-tab-cliente__info-box--prewrap">
              {cliente.note}
            </div>
          </div>
        )}
      </div>

      {/* Sottoconto contabile (dinamico) */}
      <SottocontoCogeInfo nominativoId={nominativoId} tipo="CLIENTE" categoria={cliente.categoriaCliente} />

      {/* Dati Bancari e Pagamento */}
      <div className="bop-tab-cliente__card">
        <h3 className="bop-tab-cliente__section-title">Dati Bancari e Pagamento</h3>

        <div className="bop-tab-cliente__field-grid">
          <div>
            <div className="bop-tab-cliente__field-label">Condizione di pagamento</div>
            <div className="bop-tab-cliente__field-value">
              {cliente.condizionePagamento
                ? cliente.condizionePagamento.nome
                : <span className="bop-tab-cliente__empty-text">Nessuna condizione impostata</span>}
            </div>
          </div>

          <div>
            <div className="bop-tab-cliente__field-label">Codice IVA di default</div>
            <div className="bop-tab-cliente__field-value">
              {cliente.codiceIvaDefault
                ? `${cliente.codiceIvaDefault.codice} — ${cliente.codiceIvaDefault.descrizione}`
                : <span className="bop-tab-cliente__empty-text">Nessun codice IVA impostato</span>}
            </div>
          </div>

          <div>
            <div className="bop-tab-cliente__field-label">IBAN</div>
            <div className="bop-tab-cliente__field-value">
              {cliente.iban
                ? formatIBAN(cliente.iban)
                : <span className="bop-tab-cliente__empty-text">IBAN non registrato</span>}
            </div>
          </div>

          <div>
            <div className="bop-tab-cliente__field-label">ABI / CAB</div>
            <div className="bop-tab-cliente__field-value">
              {cliente.abi || cliente.cab
                ? `${cliente.abi || '—'} / ${cliente.cab || '—'}`
                : <span className="bop-tab-cliente__empty-text">Non specificati</span>}
            </div>
          </div>

          <div>
            <div className="bop-tab-cliente__field-label">Banca</div>
            <div className="bop-tab-cliente__field-value">
              {cliente.banca || <span className="bop-tab-cliente__empty-text">Non specificata</span>}
            </div>
          </div>

          <div>
            <div className="bop-tab-cliente__field-label">Filiale</div>
            <div className="bop-tab-cliente__field-value">
              {cliente.filialeBanca || <span className="bop-tab-cliente__empty-text">Non specificata</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Informazioni Categoria Specifica */}
      {cliente.categoriaCliente === 'CATENA_GDO' && (
        <div className="bop-tab-cliente__card">
          <h3 className="bop-tab-cliente__section-title">
            Gestione GDO
          </h3>
          <div className="bop-tab-cliente__info-box">
            <p className="bop-tab-cliente__info-text">
              Per i clienti <strong>Catena GDO</strong>, è possibile gestire le filiali/punti vendita
              nella sezione <strong>Filiali</strong> del tab Anagrafica.
            </p>
          </div>
        </div>
      )}

      {cliente.categoriaCliente === 'AGENTE' && (
        <div className="bop-tab-cliente__card">
          <h3 className="bop-tab-cliente__section-title">
            Informazioni Agente
          </h3>
          <div className="bop-tab-cliente__info-box">
            <p className="bop-tab-cliente__info-text">
              Questo nominativo è categorizzato come <strong>Agente</strong>. Può essere assegnato
              ad altri clienti come agente di riferimento.
            </p>
          </div>
        </div>
      )}

      {/* Modal Modifica */}
      {showEditModal && (
        <div className="bop-modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">Modifica Dati Cliente</h3>
            </div>
            <div className="bop-modal__body">
              <div className="bop-form-group">
                <label className="bop-label">Categoria Cliente *</label>
                <select
                  className="bop-select"
                  value={form.categoriaCliente}
                  onChange={(e) => setForm({ ...form, categoriaCliente: e.target.value })}
                  disabled={saving}
                >
                  <option value="CATENA_GDO">Catena GDO</option>
                  <option value="AZIENDA_B2B">Azienda B2B</option>
                  <option value="PRIVATO_B2C">Privato B2C</option>
                  <option value="AGENTE">Agente</option>
                </select>
              </div>

              {/* Account Manager */}
              <div className="bop-form-group">
                <label className="bop-label">Account Manager</label>
                <div className="bop-tab-cliente__select-box">
                  {selectedAccount ? (
                    <div className="bop-tab-cliente__select-value">{selectedAccount.nome} {selectedAccount.cognome}</div>
                  ) : (
                    <div className="bop-tab-cliente__select-empty">Nessuno selezionato</div>
                  )}
                  <button
                    type="button"
                    className="bop-btn bop-btn-secondary bop-tab-cliente__btn-small"
                    onClick={() => setShowAccountModal(true)}
                    disabled={saving}
                  >
                    Cerca / Cambia
                  </button>
                  {selectedAccount && (
                    <button
                      type="button"
                      className="bop-tab-cliente__btn-clear"
                      onClick={() => {
                        setSelectedAccount(null);
                        setForm(prev => ({ ...prev, accountId: null }));
                      }}
                      disabled={saving}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* Insegna */}
              <div className="bop-form-group">
                <label className="bop-label">Insegna</label>
                <div className="bop-tab-cliente__select-box">
                  {selectedInsegna ? (
                    <div className="bop-tab-cliente__select-value">
                      <span className="bop-tab-cliente__mono-code">{selectedInsegna.codice}</span>
                      — {selectedInsegna.nome}
                    </div>
                  ) : (
                    <div className="bop-tab-cliente__select-empty">Nessuna selezionata</div>
                  )}
                  <button
                    type="button"
                    className="bop-btn bop-btn-secondary bop-tab-cliente__btn-small"
                    onClick={() => setShowInsegnaModal(true)}
                    disabled={saving}
                  >
                    Cerca / Cambia
                  </button>
                  {selectedInsegna && (
                    <button
                      type="button"
                      className="bop-tab-cliente__btn-clear"
                      onClick={() => {
                        setSelectedInsegna(null);
                        setForm(prev => ({ ...prev, insegnaClienteId: null }));
                      }}
                      disabled={saving}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* Agente */}
              <div className="bop-form-group">
                <label className="bop-label">Agente Assegnato</label>
                <div className="bop-tab-cliente__select-box">
                  {selectedAgente ? (
                    <div className="bop-tab-cliente__select-value">
                      <span className="bop-tab-cliente__mono-code">{selectedAgente.codice}</span>
                      — {selectedAgente.ragioneSociale || selectedAgente.nome}
                    </div>
                  ) : (
                    <div className="bop-tab-cliente__select-empty">Nessuno selezionato</div>
                  )}
                  <button
                    type="button"
                    className="bop-btn bop-btn-secondary bop-tab-cliente__btn-small"
                    onClick={() => setShowAgenteModal(true)}
                    disabled={saving}
                  >
                    Cerca / Cambia
                  </button>
                  {selectedAgente && (
                    <button
                      type="button"
                      className="bop-tab-cliente__btn-clear"
                      onClick={() => {
                        setSelectedAgente(null);
                        setForm(prev => ({ ...prev, agenteNominativoId: null }));
                      }}
                      disabled={saving}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* Dati bancari e pagamento */}
              <div className="bop-form-group">
                <label className="bop-label">Condizione di pagamento</label>
                <select
                  className="bop-select"
                  value={form.condizionePagamentoId ?? ''}
                  onChange={(e) => setForm({
                    ...form,
                    condizionePagamentoId: e.target.value ? parseInt(e.target.value, 10) : null
                  })}
                  disabled={saving}
                >
                  <option value="">— Nessuna —</option>
                  {condizioniPagamento.map(c => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>

              <div className="bop-form-group">
                <label className="bop-label">Codice IVA di default</label>
                <select
                  className="bop-select"
                  value={form.codiceIvaDefaultId ?? ''}
                  onChange={(e) => setForm({
                    ...form,
                    codiceIvaDefaultId: e.target.value ? parseInt(e.target.value, 10) : null
                  })}
                  disabled={saving}
                >
                  <option value="">— Nessuno —</option>
                  {codiciIva.map(c => (
                    <option key={c.id} value={c.id}>{c.codice} — {c.descrizione}</option>
                  ))}
                </select>
              </div>

              <BancaFormFields
                value={{ iban: form.iban, abi: form.abi, cab: form.cab, banca: form.banca, filialeBanca: form.filialeBanca }}
                onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
                disabled={saving}
              />

              <div className="bop-form-group">
                <label className="bop-label">Note</label>
                <textarea
                  className="bop-input bop-tab-cliente__textarea"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Note aggiuntive sul cliente..."
                  disabled={saving}
                />
              </div>
            </div>

            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setShowEditModal(false)} disabled={saving}>
                Annulla
              </button>
              <button className="bop-btn bop-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Account Manager */}
      {showAccountModal && (
        <ModaleCercaRecord
          titolo="Seleziona Account Manager"
          colonne={[
            { key: 'nome', label: 'Nome e Cognome' },
          ]}
          dati={utenti}
          campoRicerca={['nome']}
          onSeleziona={(record) => {
            setSelectedAccount(record);
            setForm(prev => ({ ...prev, accountId: record.id }));
            setShowAccountModal(false);
          }}
          onChiudi={() => setShowAccountModal(false)}
        />
      )}

      {/* Modale Insegna */}
      {showInsegnaModal && (
        <ModaleCercaRecord
          titolo="Seleziona Insegna"
          colonne={[
            { key: 'codice', label: 'Codice', mono: true },
            { key: 'nome', label: 'Nome Insegna' },
          ]}
          dati={insegne}
          campoRicerca={['codice', 'nome']}
          onSeleziona={(record) => {
            setSelectedInsegna(record);
            setForm(prev => ({ ...prev, insegnaClienteId: record.id }));
            setShowInsegnaModal(false);
          }}
          onChiudi={() => setShowInsegnaModal(false)}
        />
      )}

      {/* Modale Agente */}
      {showAgenteModal && (
        <ModaleCercaRecord
          titolo="Seleziona Agente"
          colonne={[
            { key: 'codice', label: 'Codice', mono: true },
            { key: 'ragioneSociale', label: 'Ragione Sociale / Nome' },
            { key: 'partitaIva', label: 'P.IVA', mono: true },
            { key: 'citta', label: 'Città' },
          ]}
          dati={agenti.map(a => ({
            ...a,
            ragioneSociale: a.ragioneSociale || a.nome || '—',
            citta: a.indirizzi?.find((i: any) => i.tipo === 'SEDE_LEGALE')?.citta || '—',
          }))}
          campoRicerca={['codice', 'ragioneSociale', 'nome']}
          onSeleziona={(record) => {
            setSelectedAgente(record);
            setForm(prev => ({ ...prev, agenteNominativoId: record.id }));
            setShowAgenteModal(false);
          }}
          onChiudi={() => setShowAgenteModal(false)}
        />
      )}

      {/* Filiali / Punti Vendita */}
      <div className="bop-card bop-card__body">
        <h3 className="bop-anagrafica__section-title">
          Filiali / Punti Vendita
          <button className="bop-btn-primary" onClick={() => openFilialeModal()}>
            <PlusIcon />
            Aggiungi
          </button>
        </h3>
        {filiali && filiali.length > 0 ? (
          <table className="bop-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Indirizzo</th>
                <th className="bop-text-center">Stato</th>
                <th className="bop-text-center">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filiali.map((fil) => {
                const indirizzoFiliale = [fil.via, fil.cap, fil.citta, fil.provincia].filter(Boolean).join(' ') || '—';
                return (
                  <tr key={fil.id}>
                    <td>{fil.nome}</td>
                    <td className="bop-text-muted">{indirizzoFiliale}</td>
                    <td className="bop-text-center">
                      <span className={`bop-badge ${fil.attivo ? 'bop-badge--success' : 'bop-badge--danger'}`}>
                        {fil.attivo ? 'Attivo' : 'Disattivato'}
                      </span>
                    </td>
                    <td className="bop-text-center">
                      <div className="bop-anagrafica__actions-cell">
                        <button className="bop-btn-secondary" onClick={() => openFilialeModal(fil)}>
                          <EditIcon />
                        </button>
                        <button className="bop-anagrafica__btn-delete" onClick={() => deleteFiliale(fil.id)}>
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="bop-empty-state">Nessuna filiale registrata</div>
        )}
      </div>

      {/* Email Cliente */}
      <div className="bop-card bop-card__body">
        <h3 className="bop-anagrafica__section-title">
          Email
          <button className="bop-btn-primary" onClick={() => openEmailModal()}>
            <PlusIcon />
            Aggiungi
          </button>
        </h3>
        {emailCliente.length > 0 ? (
          <table className="bop-table">
            <thead>
              <tr>
                <th>Etichetta</th>
                <th>Email</th>
                <th className="bop-text-center">Principale</th>
                <th className="bop-text-center">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {emailCliente.map((em) => (
                <tr key={em.id}>
                  <td>{em.etichetta}</td>
                  <td className="bop-text-muted">{em.email}</td>
                  <td className="bop-text-center">
                    {em.principale && <span className="bop-badge bop-badge--success">Principale</span>}
                  </td>
                  <td className="bop-text-center">
                    <div className="bop-anagrafica__actions-cell">
                      <button className="bop-btn-secondary" onClick={() => openEmailModal(em)}>
                        <EditIcon />
                      </button>
                      <button className="bop-anagrafica__btn-delete" onClick={() => deleteEmail(em.id)}>
                        <TrashIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="bop-empty-state">Nessuna email registrata per il cliente</div>
        )}
      </div>

      {/* Contatti Cliente */}
      <div className="bop-card bop-card__body">
        <h3 className="bop-anagrafica__section-title">
          Contatti
          <button className="bop-btn-primary" onClick={() => openContattoModal()}>
            <PlusIcon />
            Aggiungi
          </button>
        </h3>
        {contattiCliente.length > 0 ? (
          <table className="bop-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Ruolo</th>
                <th>Telefono</th>
                <th>Email</th>
                <th className="bop-text-center">Principale</th>
                <th className="bop-text-center">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {contattiCliente.map((contatto) => (
                <tr key={contatto.id}>
                  <td>{`${contatto.nome} ${contatto.cognome || ''}`.trim()}</td>
                  <td className="bop-text-muted">{contatto.ruolo || '—'}</td>
                  <td className="bop-text-muted">{contatto.telefono || contatto.cellulare || '—'}</td>
                  <td className="bop-text-muted">{contatto.email || '—'}</td>
                  <td className="bop-text-center">
                    {contatto.principale && <span className="bop-badge bop-badge--success">Principale</span>}
                  </td>
                  <td className="bop-text-center">
                    <div className="bop-anagrafica__actions-cell">
                      <button className="bop-btn-secondary" onClick={() => openContattoModal(contatto)}>
                        <EditIcon />
                      </button>
                      <button className="bop-anagrafica__btn-delete" onClick={() => deleteContatto(contatto.id)}>
                        <TrashIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="bop-empty-state">Nessun contatto registrato per il cliente</div>
        )}
      </div>

      {/* Modal Filiale */}
      {showFilialeModal && (
        <div className="bop-modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">{editingFiliale ? 'Modifica Filiale' : 'Nuova Filiale'}</h3>
            </div>
            <div className="bop-modal__body">
              <div className="bop-form-group">
                <label className="bop-label">Nome *</label>
                <input className="bop-input" value={filialeForm.nome} onChange={(e) => setFilialeForm({ ...filialeForm, nome: e.target.value })} placeholder="es. Filiale Roma" />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Via</label>
                <input className="bop-input" value={filialeForm.via} onChange={(e) => setFilialeForm({ ...filialeForm, via: e.target.value })} />
              </div>
              <div className="bop-anagrafica__form-grid-narrow-wide">
                <div className="bop-form-group">
                  <label className="bop-label">CAP</label>
                  <input className="bop-input" value={filialeForm.cap} onChange={(e) => setFilialeForm({ ...filialeForm, cap: e.target.value })} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Città</label>
                  <input className="bop-input" value={filialeForm.citta} onChange={(e) => setFilialeForm({ ...filialeForm, citta: e.target.value })} />
                </div>
              </div>
              <div className="bop-anagrafica__form-grid-narrow-wide">
                <div className="bop-form-group">
                  <label className="bop-label">Provincia</label>
                  <input className="bop-input" maxLength={2} value={filialeForm.provincia} onChange={(e) => setFilialeForm({ ...filialeForm, provincia: e.target.value.toUpperCase() })} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Paese</label>
                  <input className="bop-input" value={filialeForm.paese} onChange={(e) => setFilialeForm({ ...filialeForm, paese: e.target.value })} />
                </div>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Note</label>
                <textarea className="bop-anagrafica__textarea" value={filialeForm.note} onChange={(e) => setFilialeForm({ ...filialeForm, note: e.target.value })} />
              </div>
              <div className="bop-form-group">
                <label className="bop-anagrafica__checkbox-label">
                  <input type="checkbox" checked={filialeForm.attivo} onChange={(e) => setFilialeForm({ ...filialeForm, attivo: e.target.checked })} />
                  Filiale attiva
                </label>
              </div>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn-secondary" onClick={() => setShowFilialeModal(false)} disabled={savingRelaz}>Annulla</button>
              <button className="bop-btn-primary" onClick={saveFiliale} disabled={savingRelaz}>
                {savingRelaz ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Email */}
      {showEmailModal && (
        <div className="bop-modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">{editingEmail ? 'Modifica Email' : 'Nuova Email'}</h3>
            </div>
            <div className="bop-modal__body">
              <div className="bop-form-group">
                <label className="bop-label">Etichetta *</label>
                <input className="bop-input" value={emailForm.etichetta} onChange={(e) => setEmailForm({ ...emailForm, etichetta: e.target.value })} placeholder="es. Ufficio, Commerciale, ..." />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Email *</label>
                <input type="email" className="bop-input" value={emailForm.email} onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })} />
              </div>
              <div className="bop-form-group">
                <label className="bop-anagrafica__checkbox-label">
                  <input type="checkbox" checked={emailForm.principale} onChange={(e) => setEmailForm({ ...emailForm, principale: e.target.checked })} />
                  Email principale
                </label>
              </div>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn-secondary" onClick={() => setShowEmailModal(false)} disabled={savingRelaz}>Annulla</button>
              <button className="bop-btn-primary" onClick={saveEmail} disabled={savingRelaz}>
                {savingRelaz ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Contatto */}
      {showContattoModal && (
        <div className="bop-modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">{editingContatto ? 'Modifica Contatto' : 'Nuovo Contatto'}</h3>
            </div>
            <div className="bop-modal__body">
              <div className="bop-anagrafica__form-grid-2">
                <div className="bop-form-group">
                  <label className="bop-label">Nome *</label>
                  <input className="bop-input" value={contattoForm.nome} onChange={(e) => setContattoForm({ ...contattoForm, nome: e.target.value })} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Cognome</label>
                  <input className="bop-input" value={contattoForm.cognome} onChange={(e) => setContattoForm({ ...contattoForm, cognome: e.target.value })} />
                </div>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Ruolo</label>
                <input className="bop-input" value={contattoForm.ruolo} onChange={(e) => setContattoForm({ ...contattoForm, ruolo: e.target.value })} placeholder="es. Responsabile acquisti" />
              </div>
              <div className="bop-anagrafica__form-grid-2">
                <div className="bop-form-group">
                  <label className="bop-label">Telefono</label>
                  <input className="bop-input" value={contattoForm.telefono} onChange={(e) => setContattoForm({ ...contattoForm, telefono: e.target.value })} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Cellulare</label>
                  <input className="bop-input" value={contattoForm.cellulare} onChange={(e) => setContattoForm({ ...contattoForm, cellulare: e.target.value })} />
                </div>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Email</label>
                <input type="email" className="bop-input" value={contattoForm.email} onChange={(e) => setContattoForm({ ...contattoForm, email: e.target.value })} />
              </div>
              <div className="bop-form-group">
                <label className="bop-anagrafica__checkbox-label">
                  <input type="checkbox" checked={contattoForm.principale} onChange={(e) => setContattoForm({ ...contattoForm, principale: e.target.checked })} />
                  Contatto principale
                </label>
              </div>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn-secondary" onClick={() => setShowContattoModal(false)} disabled={savingRelaz}>Annulla</button>
              <button className="bop-btn-primary" onClick={saveContatto} disabled={savingRelaz}>
                {savingRelaz ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ApiInspector docs={API_DOCS} />
    </div>
  );
}
